import { FC, Suspense, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { mapSkillStatus } from '../../businessLogicService/mapSkillStatus';
import { SkillResource } from '../SkillResource/SkillResource';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { PreloadedQuery, useLazyLoadQuery, useQueryLoader } from 'react-relay';
import { selectUser } from '../../redux/reducers/userSlice';
import { PreviousAttempts } from '../../models/PreviousAttempts';
import { getStudentSingleSkillQuery } from '../../graphQLOperations/StudentSingleSkill';
import { TargetGroup } from '../../models/TargetGroup';
import { selectedTargetGroup, setTargetGroup } from '../../redux/reducers/targetGroupSlice';
import { StudentSkillStates } from '../../models/StudentSkillState';
import {
  PreviousAndNextSSS,
  selectedStudentSkillStateAfterTakeAssessment,
} from '../../redux/reducers/studentSkillAfterTakeAssessmentSlice';
import { inferredOrDiscrepancy } from '../../businessLogicService/inferredOrDiscrepancySubstate';
import { SkillSubStates } from '../../models/SkillState';
import { AttemptInformation } from '../../models/AttemptInformation';
import { BreadCrumb } from 'primereact/breadcrumb';
import { SkillResourceSteps } from '../../models/Timeline';
import { House } from 'lucide-react';
import { StudentSingleSkillQuery } from '../../graphQLOperations/__generated__/StudentSingleSkillQuery.graphql';
import { SkillsPresignedURLsQuery } from '../../graphQLOperations/__generated__/SkillsPresignedURLsQuery.graphql';
import { getPresignedURLsForSkill } from '../../graphQLOperations/Skills';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';

interface Props {
  forceRefetch: string;
}
export interface PreviousStatus {
  id: string;
  date: string;
  score: number;
  maxScore?: number | null;
}

export interface CurrentStatus {
  classname: string;
  status: string | undefined;
}

export const StudentSingleSkill: FC<Props> = ({ forceRefetch }) => {
  const { skillCode } = useParams();
  const dispatch = useAppDispatch();
  const userRedux = useAppSelector(selectUser);
  const targetGroupRedux = useAppSelector(selectedTargetGroup);
  const sSAfterTakeAssessmentRedux = useAppSelector(selectedStudentSkillStateAfterTakeAssessment);
  const [assessmentInProgress, setAssessmentInProgress] = useState(false);
  const [attemptInformation, setAttemptInformation] = useState<AttemptInformation | null>(null);
  const [previousAttempts, setPreviousAttempts] = useState<PreviousAttempts[]>([]);
  const [sssAfterTakeAssessment, setSSSAfterTakeAssessment] = useState<PreviousAndNextSSS>();
  const [currentStep, setCurrentStep] = useState<SkillResourceSteps | null>(null);
  const [skipNewActivityId, setSkipNewActivityId] = useState<boolean>(true);
  const [currentStatus, setCurrentStatus] = useState<CurrentStatus | null>(null);
  const [previousStatus, setPreviousStatus] = useState<PreviousStatus[]>([]);

  const {
    skillStatesByStudentId,
    currentTargetGroupByStudentId,
    skillAssessmentsByStudentId,
    allUserActions,
    latestCompletedStudentSkillDiagnostic,
  } = useLazyLoadQuery<StudentSingleSkillQuery>(
    getStudentSingleSkillQuery,
    {
      studentId: `StudentNode:${userRedux?.id}`,
      skillCode: skillCode || '',
      skillAssessmentsByStudentFilter: { skillCode: skillCode || '' },
      allUserActionsFilter: {
        userId: `UserNode:${userRedux?.id}`,
        skillCodes: [skillCode || ''],
        actionTypeId: ['ActionTypeNode:3', 'ActionTypeNode:4', 'ActionTypeNode:5'],
      },
      skip: !!targetGroupRedux?.id,
    },
    { fetchPolicy: 'network-only', fetchKey: forceRefetch },
  );

  const [skillsPresignedURLsQueryReference, loadSkillsPresignedURLsQuery] =
    useQueryLoader<SkillsPresignedURLsQuery>(getPresignedURLsForSkill);

  // A prior Skill Challenge is stale when a DG reset is pending, or when it was taken before
  // the latest completed diagnostic (the reset does not clear SK data for skills passed via SK).
  const isAssessmentStale = (sessionCompletedOn?: string | null): boolean => {
    if (latestCompletedStudentSkillDiagnostic?.retakeAssigned) return true;
    const diagnosticCompletedOn = latestCompletedStudentSkillDiagnostic?.sessionCompletedOn;
    if (!diagnosticCompletedOn || !sessionCompletedOn) return false;
    return new Date(sessionCompletedOn).getTime() < new Date(diagnosticCompletedOn).getTime();
  };

  const hasFreshAssessment = (skillAssessmentsByStudentId?.nodes ?? []).some(
    el => !isAssessmentStale(el.sessionCompletedOn),
  );

  useEffect(() => {
    if (currentTargetGroupByStudentId) {
      dispatch(setTargetGroup(currentTargetGroupByStudentId.targetGroup as TargetGroup));
    }
  }, [currentTargetGroupByStudentId]);

  useEffect(() => {
    const sSS = skillStatesByStudentId[0];
    if (sSS?.skillSubstate?.name) {
      let skillSubstate;
      let score;
      let maxScore;

      if (
        sSAfterTakeAssessmentRedux.previousState?.id === sSS.id &&
        sSAfterTakeAssessmentRedux.previousState?.skillSubstate.id === sSS.skillSubstate.id &&
        sSAfterTakeAssessmentRedux.previousState?.skillAssessment?.id === sSS.skillAssessment?.id &&
        sSAfterTakeAssessmentRedux.nextState
      ) {
        skillSubstate = inferredOrDiscrepancy(sSAfterTakeAssessmentRedux.nextState?.skillSubstate.id as string)
          ? SkillSubStates.NOT_TESTED
          : (sSAfterTakeAssessmentRedux.nextState?.skillSubstate.id as string);
        score = sSAfterTakeAssessmentRedux.nextState?.skillAssessment?.score;
        maxScore = sSAfterTakeAssessmentRedux.nextState?.skillAssessment?.maxScore;
      } else {
        skillSubstate = sSS?.skillSubstate?.id;
        score = sSS?.skillAssessment?.score;
        maxScore = sSS?.skillAssessment?.maxScore;
      }

      const { className, status } = mapSkillStatus(skillSubstate, score, maxScore);
      setCurrentStatus({ classname: className, status });
    }

    setSSSAfterTakeAssessment({
      previousState: sSS as StudentSkillStates,
      nextState:
        sSAfterTakeAssessmentRedux.nextState && sSAfterTakeAssessmentRedux.nextState.id === sSS.id
          ? sSAfterTakeAssessmentRedux.nextState
          : null,
    });
  }, [skillStatesByStudentId[0]]);

  useEffect(() => {
    if (skillAssessmentsByStudentId?.nodes) {
      setPreviousAttempts((skillAssessmentsByStudentId.nodes as unknown as PreviousAttempts[]) || []);

      setPreviousStatus(
        skillAssessmentsByStudentId.nodes
          .map(el => {
            return {
              id: el.id,
              date: el.sessionCompletedOn,
              score: el.score,
              maxScore: el.maxScore,
            };
          })
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
      );
    }
  }, [skillAssessmentsByStudentId]);

  // Restoring the student's last position is page-load initialization, not a continuous
  // derivation, so it must run at most once per skill. `allUserActions` is a fresh object on every
  // query resolution, and entering the Skill Challenge deliberately logs no UserAction (see the
  // `option !== SKILL_CHALLENGE` branch in TimeLineWrapper.onClickHandler). So re-running this
  // replays the *previous* step and rewinds a student out of an in-flight attempt, while
  // `assessmentInProgress` stays true — hiding the breadcrumb, header and timeline around
  // About-the-Skill content. A refetch is not hypothetical: any Relay store reset does it.
  const restoredStepForSkill = useRef<string | null>(null);

  useEffect(() => {
    const skill = skillCode ?? '';
    if (restoredStepForSkill.current === skill) return;
    restoredStepForSkill.current = skill;

    if (allUserActions?.nodes?.length) {
      if (allUserActions.nodes[0].description === 'Practice Problems')
        setCurrentStep(SkillResourceSteps.PRACTICE_PROBLEMS);
      if (allUserActions.nodes[0].description === 'Worked Example Video')
        setCurrentStep(SkillResourceSteps.WORKED_EXAMPLE);
      if (allUserActions.nodes[0].description === 'About the skill') setCurrentStep(SkillResourceSteps.ABOUT_THE_SKILL);
      if (allUserActions.nodes[0].description === 'Skill Challenge') {
        // Only reopen in review mode if the latest Skill Challenge is still valid, i.e. it was
        // taken after the latest diagnostic. Pre-reset attempts fall back to About the Skill.
        if (skillStatesByStudentId[0]?.skillAssessment && hasFreshAssessment) {
          //open in a review mode
          setCurrentStep(SkillResourceSteps.SKILL_CHALLENGE);
        } else {
          setCurrentStep(SkillResourceSteps.ABOUT_THE_SKILL);
        }
      }
    } else {
      setCurrentStep(SkillResourceSteps.ABOUT_THE_SKILL);
    }
  }, [allUserActions, hasFreshAssessment, skillCode]);

  useEffect(() => {
    loadSkillsPresignedURLsQuery({
      skillCode: skillCode || '',
      filter: { storedObjectType: 'WORKED_EXAMPLE_VIDEO' },
    });
  }, [skillCode]);

  const breadcrumbItems = [
    {
      label: skillStatesByStudentId[0]?.skill?.name || '',
      command: () => setCurrentStep(0),
      template: () => (
        <span className="tw-font-atkinson tw-text-sm tw-text-stone-900 tw-font-bold">
          {skillStatesByStudentId[0]?.skill?.name || ''}
        </span>
      ),
    },
  ];

  const home = {
    template: () => (
      <Link
        to={'/'}
        className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-font-atkinson tw-text-neutral-700"
      >
        <House className="tw-text-blue-500" /> My top picks
      </Link>
    ),
  };

  return (
    <Suspense fallback={<ThreeDotLoader dark />}>
      <div
        style={{ overflowX: 'hidden' }}
        className={`nc_page-inner xl:tw-w-10/12 tw-mx-auto tw-w-11/12 ${
          assessmentInProgress ? 'paper-grid-bg nc_skill-challenge-bg' : ''
        }`}
      >
        {!assessmentInProgress && (
          <BreadCrumb model={breadcrumbItems} home={home} className="col-12 col-offset-0 tw-py-0" />
        )}
        {skillStatesByStudentId && targetGroupRedux?.id && skillsPresignedURLsQueryReference && (
          <SkillResource
            setAttemptInformation={setAttemptInformation}
            attemptInformation={attemptInformation}
            setPreviousAttempts={setPreviousAttempts}
            previousAttempts={previousAttempts}
            studentStateInfo={skillStatesByStudentId}
            setCurrentStatus={setCurrentStatus}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            sssAfterTakeAssessment={sssAfterTakeAssessment as PreviousAndNextSSS}
            skipNewActivityId={skipNewActivityId}
            setSkipNewActivityId={setSkipNewActivityId}
            skillsPresignedURLsQueryReference={
              skillsPresignedURLsQueryReference as PreloadedQuery<SkillsPresignedURLsQuery, Record<string, unknown>>
            }
            previousStatus={previousStatus}
            setPreviousStatus={setPreviousStatus}
            currentStatus={currentStatus}
            allUserActions={allUserActions}
            assessmentInProgress={assessmentInProgress}
            setAssessmentInProgress={setAssessmentInProgress}
          />
        )}
      </div>
    </Suspense>
  );
};
